import React, { useEffect, useRef, useState } from 'react';
import { Accordion } from 'react-accessible-accordion';
import { Loader } from 'component/Loader';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { getEoreReportById, getHandouts } from 'api/EOREApi';
import { getDonorById } from 'api/DonorsApi';
import { getCustomerById } from 'api/CustomersApi';
import { useValidationComments } from 'hooks/useValidationComments';
import { CommentBlock, CommentDisplay, handleReportValidation } from 'component/CommentBlock';
import { EOREFormData } from 'consts/Forms';
import { ButtonComponent } from 'component/Button';
import { REPORT_TYPES } from 'consts/ReportValidation';
import { rejectEOREReport, localRejectEOREReport } from 'api/HaApi';
import { useUser } from 'routes/UserContext';
import { FinalIndicatorsView } from './Blocks/View/FinalIndicators/FinalIndicators';
import { SessionInfo } from './Blocks/View/SessionInfo/SessionInfo';
import { PageTitle } from './Create';
import { getAttachments, getEorePhotos } from './Blocks/PhotosBlock';
import GeneralInfo from './Blocks/View/GeneralInfo/GeneralInfo';
import PhotosBlockView from './Blocks/View/PhotosBlockView/PhotosBlockView';
import AttachmentsBlockView from './Blocks/View/AttachmentsBlockView/AttachmentsBlockView';
import { CommentsFromImsmaView } from 'pages/Reports/ViewBlocks/Comments';
import { TranslateWrapperComponent } from 'component/Translation';
import { ReactComponent as TranslateSvg } from 'assets/images/icons/translate_24px.svg';

export const ViewEOREReportPage = ({ mode }) => {
  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState(EOREFormData);
  const [templateVersion, setTemplateVersion] = useState('');
  const [handouts, setHandouts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const photosRef = useRef([]);
  photosRef.current = photos;
  const [attachments, setAttachments] = useState([]);
  const [prefetchedDonorName, setPrefetchedDonorName] = useState(undefined);
  const [prefetchedCustomerName, setPrefetchedCustomerName] = useState(undefined);
  const [zone, setZone] = useState('');
  const { reportId } = useParams();
  const commentsData = useValidationComments(REPORT_TYPES.EORE, reportId);
  const {
    comments,
    commentForm,
    savedComment,
    commentEntered,
    onSaveComment,
    onDeleteComment,
    handleComments,
    resetCommentForm,
  } = commentsData;

  const navigate = useNavigate();
  const { user } = useUser();
  const [translateState, setTranslateState] = useState(false);
  const [translatedFormData, setTranslatedFormData] = useState({});

  useEffect(() => {
    getEoreReportById(reportId)
      .then(async (res) => {
        setFormData(res.data);
        if (res.data?.zone) {
          setZone(res.data.zone);
        }
        const donorRaw = res.data?.donor ?? res.data?.info?.general?.donor ?? res.data?.info?.donor;
        const donorId = donorRaw && typeof donorRaw === 'object'
          ? (donorRaw?.value ?? donorRaw?.id)
          : donorRaw;
        const customerRaw = res.data?.info?.general?.task_customer ?? res.data?.task_customer;
        const customerId = customerRaw && typeof customerRaw === 'object'
          ? (customerRaw?.value ?? customerRaw?.id)
          : customerRaw;

        const prefetchPromises = [];

        if (donorId) {
          prefetchPromises.push(
            getDonorById(donorId)
              .then((response) => {
                setPrefetchedDonorName(response?.data?.name ?? null);
              })
              .catch(() => {
                setPrefetchedDonorName(null);
              })
          );
        } else {
          setPrefetchedDonorName(null);
        }

        if (customerId) {
          prefetchPromises.push(
            getCustomerById(customerId)
              .then((response) => {
                setPrefetchedCustomerName(response?.data?.name ?? null);
              })
              .catch(() => {
                setPrefetchedCustomerName(null);
              })
          );
        } else {
          setPrefetchedCustomerName(null);
        }

        await Promise.all(prefetchPromises);

        await getEorePhotos(res.data.id, res.data.info.general_photos, setPhotos);
        getAttachments(res.data, setAttachments);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    return () => {
      photosRef.current.forEach((p) => { if (p?.data_url) URL.revokeObjectURL(p.data_url); });
    };
  }, []);

  useEffect(() => {
    setTemplateVersion(formData?.template_version);
  }, [formData?.template_version]);

  useEffect(() => {
    getHandouts()
      .then((res) => {
        setHandouts(res.data?.map((handout) => ({
          handout_id: handout.id,
          name: handout.name,
          count: 0,
        })));
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const handleValidation = (status, event) => handleReportValidation(
    status,
    event,
    `/validations/eore/${reportId}/`,
    '/reports/eore/',
    setLoader,
    savedComment,
    resetCommentForm,
    navigate,
  );

  const handleRejectButton = (status) => {
    setLoader(true);
    rejectEOREReport(formData?.id, { status })
      .then(() => {
        toast.success('EORE report rejected from IMSMA');
        setLoader(false);
        setTimeout(() => {
          navigate('/reports/eore');
        }, 2000);
      })
      .catch(() => {
        toast.error('Error rejecting EORE report from IMSMA');
        setLoader(false);
      });
  };

  const handleLocalRejectButton = (status) => {
    setLoader(true);
    localRejectEOREReport(formData?.id, { status })
      .then(() => {
        toast.success('EORE report rejected from DMS');
        setLoader(false);
        setTimeout(() => {
          navigate('/reports/eore');
        }, 2000);
      })
      .catch(() => {
        toast.error('Error rejecting EORE report from DMS');
        setLoader(false);
      });
  };
  return (
    <section>
      <PageTitle
        title={`${mode === 'validation' ? 'Validation' : 'View'} EORE report`}
        formData={formData}
        templateVersion={templateVersion}
        setLoader={setLoader}
      />
      <TranslateWrapperComponent
        formData={{ ...formData, report_type: 'eore_report' }}
        setTranslatedFormData={setTranslatedFormData}
        translationState={translateState}
      >
        <div className="text-hover flex-group flex-right" onClick={() => setTranslateState(!translateState)}>
          <TranslateSvg />
          <span>Translate</span>
        </div>

        <div className="info_block_header">
          <div className="info_block_header_left">Атрибути</div>
          <span className="info_block_header_line" />
          <div className="info_block_header_right">Attributes</div>
        </div>
        <div className="form-wrapper">
          <Accordion
            allowMultipleExpanded
            allowZeroExpanded
            preExpanded={[
              'general',
              'sessionInformation',
              'finalIndicators',
            ]}
            className="custom-accordion"
          >
              {(mode === 'validation' || mode === 'view') && <CommentsFromImsmaView comments={comments} />}
            <GeneralInfo
              formData={formData}
              setFormData={setFormData}
              mode={mode}
              comments={[]}
              prefetchedDonorName={prefetchedDonorName}
              prefetchedCustomerName={prefetchedCustomerName}
              translatedFormData={translatedFormData}
              translateState={translateState}
            />
            {mode === 'validation' && (
              <CommentBlock
                comments={comments}
                savedComment={savedComment}
                commentForm={commentForm}
                commentEntered={commentEntered}
                block="general"
                onDeleteComment={onDeleteComment}
                handleComments={handleComments}
                onSaveComment={onSaveComment}
              />
            )}
            {mode === 'view' && (
              <CommentDisplay comments={comments} block="general" />
            )}
            <SessionInfo
              formData={formData}
              handouts={handouts}
              zone={zone}
              setZone={setZone}
              setFormData={setFormData}
              mode={mode}
              translatedFormData={translatedFormData}
              translateState={translateState}
            />
            {mode === 'validation' && (
              <CommentBlock
                comments={comments}
                savedComment={savedComment}
                commentForm={commentForm}
                commentEntered={commentEntered}
                block="session_information"
                onDeleteComment={onDeleteComment}
                handleComments={handleComments}
                onSaveComment={onSaveComment}
              />
            )}
            {mode === 'view' && (
              <CommentDisplay comments={comments} block="session_information" />
            )}
            <FinalIndicatorsView
              formData={formData}
            />
            {mode === 'validation' && (
              <CommentBlock
                comments={comments}
                savedComment={savedComment}
                commentForm={commentForm}
                commentEntered={commentEntered}
                block="final_indicators"
                onDeleteComment={onDeleteComment}
                handleComments={handleComments}
                onSaveComment={onSaveComment}
              />
            )}
            {mode === 'view' && (
              <CommentDisplay comments={comments} block="final_indicators" />
            )}
            <PhotosBlockView
              formData={formData}
              photos={photos}
              setLoader={setLoader}
            />
            <AttachmentsBlockView
              attachments={attachments}
              setLoader={setLoader}
              formData={formData}
            />
            {mode === 'validation' && (
              <div className="form-buttons-end buttons-group py-10">
                <ButtonComponent
                  handleButton={(event) => handleValidation('rejected', event)}
                  label="Reject"
                  disabled={!Object.values(savedComment).some((comment) => comment && comment.trim() !== '')}
                  className="danger"
                  type="button"
                />
                <ButtonComponent
                  handleButton={(event) => handleValidation('accepted', event)}
                  label="Accept"
                  disabled={false}
                  type="button"
                />
              </div>
            )}
          </Accordion>
            {formData?.validation_status?.value === 'validated'
              && user?.permissions?.reject_imsma_eore
              && formData?.send_to_imsma
              && (
                <div className="form-buttons buttons-group py-10">
                <ButtonComponent
                  handleButton={() => {
                    handleRejectButton('rejected');
                  }}
                  label="Reject from IMSMA"
                  disabled={false}
                  className="danger"
                  type="button"
                />
          </div>
              )}
            {formData?.validation_status?.value === 'validated'
              && user?.permissions?.reject_imsma_eore
              && !formData?.send_to_imsma
              && (
                <div className="form-buttons buttons-group py-10">
                <ButtonComponent
                  handleButton={() => {
                    handleLocalRejectButton('rejected');
                  }}
                  label="Reject from DMS"
                  disabled={false}
                  className="danger"
                  type="button"
                />
          </div>
              )}
        </div>
      </TranslateWrapperComponent>
      <Loader visible={loader} />
      <ToastContainer />
    </section>
  );
};
